import { useEffect, useRef } from 'react'
import { Editor, EditorContent, useEditor, BubbleMenu } from '@tiptap/react'
import { MenuBarEditor } from 'src/pages/CommunityDetails/Posts/tiptap/menuBarEditor'
import CharacterCount from '@tiptap/extension-character-count'
import Document from '@tiptap/extension-document'
import Text from '@tiptap/extension-text'
import Paragraph from '@tiptap/extension-paragraph'
import Placeholder from '@tiptap/extension-placeholder'
import { EditorView } from '@tiptap/pm/view'
import Bold from '@tiptap/extension-bold'
import Italic from '@tiptap/extension-italic'
import Underline from '@tiptap/extension-underline'
import Strike from '@tiptap/extension-strike'
import HardBreak from '@tiptap/extension-hard-break'
import clsx from 'clsx'
import { sanitizeHtml } from 'src/pages/CommunityDetails/Posts/tiptap/utils'
import { useMention, useMentionUtils } from '../../hooks'
import { Wrapper } from './richTextInput.styles'
import { RichTextInputProps } from './richTextInput.types'
import { COMMENT_LIMIT } from './richTextInput.constants'

const stripParagraphs = (value: string) => {
  return value.replace(/<\/?p>/g, '').trim()
}

const RichTextInput = ({
  value,
  autoFocus,
  commentSuggestionText,
  placeholder,
  readOnly,
  blur,
  enableMentions,
  enableMenuEditor,
  enableBreakLine,
  className,
  onChange,
  onFocus,
  onKeyDown,
  onMentionSuccess,
  onMentionError,
  onMentionClick,
  allowedTags = [],
  allowedTagsAttributes = []
}: RichTextInputProps) => {
  const mentionContainerRef = useRef<HTMLDivElement>(null)
  const { mentionsEditorExtensions } = useMention({
    enable: enableMentions,
    mentionConfig: {
      appendTo: /* istanbul ignore next */ () => mentionContainerRef.current,
      zIndex: 2
    },
    onMentionSuccess,
    onMentionError,
    onMentionClick
  })

  const { extractMentionsFromHTML, parseMentions } = useMentionUtils()

  const handleParseMentions = (editor: Editor) => {
    const commentHTML = editor.getHTML()
    const mentions = extractMentionsFromHTML(commentHTML)
    const parsedComment = parseMentions({ text: commentHTML, mentions })
    const value = enableMenuEditor ? parsedComment : stripParagraphs(parsedComment)
    onChange?.({ value, mentions })
  }

  /* istanbul ignore next */
  const editor = useEditor({
    immediatelyRender: true,
    shouldRerenderOnTransaction: false,
    autofocus: autoFocus ? 'end' : false,
    ...(!enableBreakLine && {
      editorProps: {
        handleDOMEvents: {
          keydown: (_: EditorView, event: KeyboardEvent) => {
            return event.key === 'Enter'
          }
        }
      }
    }),
    extensions: [
      Document,
      Text,
      Paragraph,
      ...(enableBreakLine
        ? [
            HardBreak.extend({
              addKeyboardShortcuts() {
                return {
                  Enter: () => this.editor.commands.setHardBreak()
                }
              }
            })
          ]
        : []),
      ...(enableMenuEditor ? [Bold, Italic, Underline, Strike] : []),
      sanitizeHtml({ allowedTags, allowedTagsAttributes }),
      CharacterCount.configure({
        limit: COMMENT_LIMIT
      }),
      ...mentionsEditorExtensions,
      Placeholder.configure({
        placeholder
      })
    ],
    content: value,
    onUpdate: ({ editor }) => {
      handleParseMentions(editor)
    },
    onFocus: () => {
      onFocus?.()
    }
  })

  const updateValue = (value: string) => {
    editor?.commands.setContent(value, false, {
      preserveWhitespace: 'full'
    })
  }

  const showMenuEditor = editor && enableMenuEditor

  useEffect(() => {
    if (!value.trim().length) {
      updateValue('')
      editor?.commands.setContent('')
    }
  }, [value])

  useEffect(() => {
    if (blur) {
      editor?.commands.blur()
    }
  }, [editor, blur])

  useEffect(() => {
    if (autoFocus) {
      handleParseMentions(editor)
    }
  }, [autoFocus])

  useEffect(() => {
    if (commentSuggestionText) {
      updateValue(commentSuggestionText)
      handleParseMentions(editor)
    }
  }, [commentSuggestionText])

  useEffect(() => {
    editor.setEditable(!readOnly)
  }, [readOnly])

  return (
    <Wrapper ref={mentionContainerRef} className="_position-relative" readOnly={readOnly}>
      <EditorContent
        editor={editor}
        onKeyDown={onKeyDown}
        className={clsx(className, {
          'editor-autofocus': autoFocus,
          'editor-blur': blur
        })}
      />

      {showMenuEditor && (
        <BubbleMenu
          shouldShow={({ from, to }) => from !== to}
          className="bubble-bar-menu"
          editor={editor}
          tippyOptions={{ placement: 'bottom' }}
        >
          <MenuBarEditor editor={editor} />
        </BubbleMenu>
      )}
    </Wrapper>
  )
}

export default RichTextInput
